import { WalletProvider } from "@solomon-apps/shared";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { PositionWalkthroughLayout } from "@/pages/AddPosition/PositionWalkthrough";
import { PositionsEmptyState } from "@/pages/Positions/components/PositionsEmptyState";
import { isSimMode } from "@/services/sim/simMode";

import {
    ADD_POSITION_SHOWCASE_STATES,
    type AddPositionShowcaseState
} from "./addPositionShowcaseStates";

const NO_OP = (): void => undefined;

const GROUPS: { id: AddPositionShowcaseState["group"]; title: string }[] = [
    { id: "enroll", title: "Step 1: Add Address" },
    { id: "rewards", title: "Step 2: Rewards Destination (Optional)" },
    { id: "submit", title: "Step 3: Submit" }
];

/**
 * Design-QA showcase for the Company Recipient Console's Add a Position flow
 * (`/showcase/add-position`, dev bundles only). Every state of the flow is
 * stacked full width, each rendered by the REAL layout
 * (`PositionWalkthroughLayout`: step rail, bordered frame with the in-box
 * action, and the rail-anchored Back/Next) from a fixture `StepFlow`, so the
 * placement of the step action against the wizard nav can be judged across
 * every state. Runs in sim mode so the saved-destination picker and the live
 * first-run host read the sim data layer instead of the org API.
 */
export function AddPositionShowcase(): React.ReactElement | null {
    const [simReady] = useState(isSimMode);

    // Sim mode must be on at page load (the sim session signs in before React
    // renders), so a visit without it reloads once with `?sim=1`.
    useEffect(() => {
        if (!simReady) {
            window.location.replace(`${window.location.pathname}?sim=1`);
        }
    }, [simReady]);

    if (!simReady) return null;

    return (
        <div className="min-h-screen bg-surface-ground py-14">
            <div className="mx-auto flex max-w-7xl flex-col px-6">
                <Text variant="h1" className="text-foreground-base">
                    Add an Address: every state
                </Text>
                <Text
                    variant="body"
                    className="mt-2 max-w-3xl text-foreground-muted"
                >
                    The real step rail, frame and Back/Next, fed fixed wizard
                    state. The step action (Verify Ownership, Add Address) sits
                    inside the bordered frame; Back/Next sit under the step
                    rail, bottom-aligned with the frame.
                </Text>

                <nav aria-label="States" className="mt-8 flex flex-col gap-4">
                    {GROUPS.map((group) => (
                        <div key={group.id} className="flex flex-col gap-1.5">
                            <Text
                                variant="label"
                                className="text-foreground-muted"
                            >
                                {group.title}
                            </Text>
                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                                {ADD_POSITION_SHOWCASE_STATES.filter(
                                    (s) => s.group === group.id
                                ).map((state) => (
                                    <a key={state.id} href={`#${state.id}`}>
                                        <Text
                                            variant="small"
                                            render={<span />}
                                            className="text-foreground-base underline-offset-4 hover:underline"
                                        >
                                            {state.title}
                                        </Text>
                                    </a>
                                ))}
                            </div>
                        </div>
                    ))}
                    <a href="#hosts">
                        <Text
                            variant="label"
                            render={<span />}
                            className="text-foreground-muted hover:underline"
                        >
                            Hosts: first-run, /positions/add, mobile
                        </Text>
                    </a>
                </nav>

                {GROUPS.map((group) => (
                    <section
                        key={group.id}
                        className="mt-16 flex flex-col gap-12"
                    >
                        <Text
                            variant="h2"
                            render={<h2 />}
                            className="text-foreground-base"
                        >
                            {group.title}
                        </Text>
                        {ADD_POSITION_SHOWCASE_STATES.filter(
                            (s) => s.group === group.id
                        ).map((state) => (
                            <StateBlock key={state.id} state={state} />
                        ))}
                    </section>
                ))}

                <HostsSection />
            </div>
        </div>
    );
}

function StateBlock({
    state
}: {
    state: AddPositionShowcaseState;
}): React.ReactElement {
    return (
        <article
            id={state.id}
            className="flex scroll-mt-8 flex-col gap-4 border-t border-line-base pt-6"
        >
            <div className="flex flex-col gap-1">
                <Text
                    variant="h3"
                    render={<h3 />}
                    className="text-foreground-base"
                >
                    {state.title}
                </Text>
                <Text
                    variant="small"
                    className="max-w-3xl text-foreground-muted"
                >
                    {state.caption}
                </Text>
            </div>
            <PositionWalkthroughLayout
                flow={state.flow}
                viewOnly={false}
                onSubmitted={NO_OP}
                rewardsVariant={state.rewardsVariant}
            />
        </article>
    );
}

/**
 * The flow in its hosts. The first-run host is the real `PositionsEmptyState`,
 * live (its own `useStepFlow`, sim data and sim wallet), wrapped in the wallet
 * provider the console layout normally supplies. The `/positions/add` framing
 * and the mobile DesktopOnly placeholder are inline in `PositionsPage`, so they
 * are linked on their real routes rather than copied here.
 */
function HostsSection(): React.ReactElement {
    return (
        <section id="hosts" className="mt-16 flex scroll-mt-8 flex-col gap-12">
            <Text variant="h2" render={<h2 />} className="text-foreground-base">
                Hosts
            </Text>
            <article className="flex flex-col gap-4 border-t border-line-base pt-6">
                <div className="flex flex-col gap-1">
                    <Text
                        variant="h3"
                        render={<h3 />}
                        className="text-foreground-base"
                    >
                        First run: Start Earning (live)
                    </Text>
                    <Text
                        variant="small"
                        className="max-w-3xl text-foreground-muted"
                    >
                        The /positions empty state, interactive on sim data and
                        the sim wallet.
                    </Text>
                </div>
                <WalletProvider>
                    <PositionsEmptyState viewOnly={false} onSubmitted={NO_OP} />
                </WalletProvider>
            </article>
            <article className="flex flex-col gap-2 border-t border-line-base pt-6">
                <Text
                    variant="h3"
                    render={<h3 />}
                    className="text-foreground-base"
                >
                    /positions/add and mobile
                </Text>
                <Text
                    variant="small"
                    className="max-w-3xl text-foreground-muted"
                >
                    Add a Position under the key stats, and at phone width the
                    Add an address on desktop placeholder. Open on the real
                    route (sim session):
                </Text>
                <Link to="/positions/add?sim=1" className="self-start">
                    <Text
                        variant="small"
                        render={<span />}
                        className="text-foreground-base underline underline-offset-4"
                    >
                        /positions/add?sim=1
                    </Text>
                </Link>
            </article>
        </section>
    );
}
